import {
  Close,
  EditOutlined,
  Functions,
  InfoOutlined,
  LockOutlined,
  PersonAddAlt1Outlined,
  SearchOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Popover,
  Tooltip,
} from "@mui/material";
import { isNil } from "lodash";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import SelectUserEmailInput from "../../../../components/inputs/SelectUserEmailInput";
import Flx from "../../../../components/layout/Flx";
import BasicModal from "../../../../components/modals/BasicModal";
import LoanSearchModal from "../../../../components/modals/common/LoanSearchModal";
import Txt from "../../../../components/typography/Txt";
import UserAvatar from "../../../../components/ui/UserAvatar";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import { isConstructionProduct } from "../../../../utils/loan/productType";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import { fmtDuration } from "../LoanDrilldownKeyDatesAndDocumentsTab/cards/_shared";
import {
  Card,
  CardHead,
  FONT_MONO,
  FONT_SERIF,
  KpiRow,
  SHADOW_MD,
  StatusChanger,
  T,
} from "./summaryShared";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

const dollar = (v) => (isNil(v) ? "—" : formatDollar(v, "—"));
const pct = (v) => (isNil(v) ? "—" : formatPercent(v));

// Render an operand as "Label (formattedValue)" so a tooltip is self-
// explanatory without the reader needing to know schema field names.
const operand = (label, value) => `${label} (${value})`;

// Single source of truth for every computed-field tooltip on this tab. Both
// the top-card metrics strip and the bento-grid field rows pull from this
// map so a metric's formula reads identically wherever it appears. Each
// formula is a multi-line string (newlines preserved by `whiteSpace:
// pre-line` in the tooltip rendering); first line(s) describe the inputs,
// last line shows the computed result.
const buildLoanFormulas = (loan) => {
  const baseLoan = loan?.baseLoanAmount;
  const totalLoan = loan?.totalLoanAmount;
  const holdback = loan?.totalHoldback;
  const totalCost = loan?.totalCostBasis;
  const arv = loan?.totalArv;
  const asIs = loan?.totalAsIsValue;
  const purchase = loan?.totalPurchasePrice;
  const interestReserve = loan?.totalInterestReserves;
  const totalRehab = loan?.totalConstructionBudget;
  const assignmentFees = loan?.totalAssignmentFees;
  const noteRate = loan?.interestRate;

  const propCount = loan?.subjectProperties?.length || 0;
  const propWord = propCount === 1 ? "property" : "properties";
  const sumAcross = `Sum across ${propCount} ${propWord}`;

  // Derived (mirror the computations the cards/strip make locally so the
  // tooltip's "= result" line reflects exactly what's rendered).
  const monthlyPayment =
    !isNil(totalLoan) && !isNil(noteRate) ? (totalLoan * noteRate) / 12 : null;
  const monthsOfReserve =
    !isNil(interestReserve) && monthlyPayment
      ? interestReserve / monthlyPayment
      : null;
  const costToArv =
    !isNil(totalCost) && !isNil(arv) && arv !== 0 ? totalCost / arv : null;
  const costSum =
    isNil(purchase) && isNil(totalRehab)
      ? null
      : (purchase || 0) + (totalRehab || 0);
  const borrowerFinancedRehab =
    !isNil(totalRehab) && !isNil(holdback)
      ? Math.max(0, totalRehab - holdback)
      : null;

  return {
    totalLoanAmount: `${operand("Initial Loan Amount", dollar(baseLoan))}\n+ ${operand("Rehab Holdback", dollar(holdback))}\n= ${dollar(totalLoan)}`,
    interestReserve: `${sumAcross} of per-property Interest Reserves\n= ${dollar(interestReserve)}`,
    totalCost: `${sumAcross} of Cost Basis\n(Purchase Price + Assignment Fees + Additional Basis)\n= ${dollar(totalCost)}`,
    afterRepairValue: `${sumAcross} of ARV\n= ${dollar(arv)}`,
    purchasePrice: `${sumAcross} of Purchase Price\n= ${dollar(purchase)}`,
    assignmentFees: `${sumAcross} of Assignment Fees\n= ${dollar(assignmentFees)}`,
    totalRehab: `${sumAcross} of Construction Budget\n= ${dollar(totalRehab)}`,
    borrowerFinancedRehab: `${operand("Total Rehab", dollar(totalRehab))}\n− ${operand("Rehab Holdback", dollar(holdback))}\n= ${dollar(borrowerFinancedRehab)}`,
    cost: `${operand("Total Purchase Price", dollar(purchase))}\n+ ${operand("Total Rehab Budget", dollar(totalRehab))}\n= ${dollar(costSum)}`,
    costToArv: `${operand("Total Cost", dollar(totalCost))}\n÷ ${operand("After Repair Value", dollar(arv))}\n= ${pct(costToArv)}`,
    loanToPurchasePrice: `${operand("Initial Loan Amount", dollar(baseLoan))}\n÷ ${operand("Purchase Price", dollar(purchase))}\n= ${pct(loan?.baseLoanToPurchasePrice)}`,
    loanToValue: `${operand("Initial Loan Amount", dollar(baseLoan))}\n÷ ${operand("Total As-Is Value", dollar(asIs))}\n= ${pct(loan?.baseLoanToAsIsValue)}`,
    loanToCost: `${operand("Initial Loan Amount", dollar(baseLoan))}\n÷ ${operand("Total Cost", dollar(totalCost))}\n= ${pct(loan?.baseLoanToCost)}`,
    loanToArv: `${operand("Total Loan Amount", dollar(totalLoan))}\n÷ ${operand("After Repair Value", dollar(arv))}\n= ${pct(loan?.totalLoanToArv)}`,
    monthlyPayment: `${operand("Total Loan Amount", dollar(totalLoan))} × ${operand("Note Rate", pct(noteRate))} ÷ 12\n= ${dollar(monthlyPayment)}`,
    monthsOfReserve: `${operand("Total Interest Reserves", dollar(interestReserve))}\n÷ ${operand("Monthly Payment", dollar(monthlyPayment))}\n= ${monthsOfReserve != null ? monthsOfReserve.toFixed(2) : "—"}`,
  };
};

/* This module exports the bento-grid primitives (BentoGrid, BentoColumn) and
   the per-section row-builder hooks / cards consumed by LoanDrilldownSummaryTab
   and the Closer View. It has no tab of its own. */

/* ─── Top bar ──────────────────────────────────────────────
   Breadcrumb-style nav strip ported from SummaryRedesignAltTab.
   The loan number is rendered as a clickable chip that opens
   the same LoanSearchModal triggered by the header search icon
   — so users can quickly hop to a different loan without
   leaving this screen. */
const TopBar = ({ loan }) => {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <>
      <Flx ac jb g={3} wrap sx={{ mb: 3 }}>
        <Flx ac g={1}>
          <BreadLink onClick={() => navigate("/loans")}>Loans</BreadLink>
          <BreadSep />
          <BreadLink
            onClick={() => navigate(`/loans/${loan?.pipeline || "all"}`)}
          >
            {loan?.pipeline ? `${loan?.pipeline} Pipeline` : "Pipeline"}
          </BreadLink>
          <BreadSep />
          <Tooltip title="Search loans" arrow>
            <Box
              component="button"
              type="button"
              onClick={() => setSearchOpen(true)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontFamily: FONT_MONO,
                fontSize: 12,
                color: T.ink,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                cursor: "pointer",
                px: 0.75,
                py: "2px",
                borderRadius: "4px",
                border: `1px solid ${T.line2}`,
                background: T.surface2,
                transition: "background 0.15s, border-color 0.15s, color 0.15s",
                "&:hover": {
                  background: T.surface,
                  borderColor: T.line,
                  color: T.ink,
                  "& svg": { opacity: 1 },
                },
                "& svg": { opacity: 0.55, transition: "opacity 0.15s" },
              }}
            >
              <SearchOutlined sx={{ fontSize: 12 }} />#{loan?.loanNumber || "—"}
            </Box>
          </Tooltip>
        </Flx>
      </Flx>
      <LoanSearchModal show={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

const BreadLink = ({ children, onClick }) => (
  <Box
    component="span"
    onClick={onClick}
    sx={{
      fontFamily: FONT_MONO,
      fontSize: 12,
      color: T.ink3,
      cursor: "pointer",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      "&:hover": { color: T.ink },
    }}
  >
    {children}
  </Box>
);

const BreadSep = () => (
  <Txt sx={{ color: T.ink4, fontSize: 12, fontFamily: FONT_MONO }}>/</Txt>
);

/* ─── Top card ─────────────────────────────────────────────
   Mirrors Hero from SummaryRedesignAltTab (title + status
   changer + stage tracker), but with a metrics strip docked
   below the progress bar so all 15 headline figures from the
   reference screenshot live inside the same card. */
const TopCard = ({ loan }) => {
  const firstAddress =
    addressLabel(loan?.subjectProperties?.[0]?.address) || null;
  const firstBorrower = loan?.borrowers?.[0];
  const borrowerName = firstBorrower
    ? `${firstBorrower?.firstName || ""} ${firstBorrower?.lastName || ""}`.trim()
    : null;

  const title = useMemo(() => {
    if (loan?.loanName) return loan.loanName;
    const parts = [borrowerName, firstAddress].filter(Boolean);
    return parts.length ? parts.join(" — ") : `Loan #${loan?.loanNumber}`;
  }, [loan?.loanName, loan?.loanNumber, borrowerName, firstAddress]);

  const lastUpdatedRel = useMemo(() => {
    if (!loan?.updateDatetime) return null;
    const ms = Date.now() - new Date(loan.updateDatetime).getTime();
    if (Number.isNaN(ms)) return null;
    const days = Math.round(ms / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  }, [loan?.updateDatetime]);

  return (
    <Box
      sx={{
        background: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: "12px",
        boxShadow: SHADOW_MD,
        p: { xs: "22px", md: "28px 32px" },
        mb: 3,
      }}
    >
      <Flx wrap g={3} jb sx={{ alignItems: "flex-start", mb: "28px" }}>
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Txt
            component="h1"
            sx={{
              fontFamily: FONT_SERIF,
              fontWeight: 500,
              fontSize: 28,
              letterSpacing: "-0.02em",
              m: 0,
              mb: 1,
              color: T.ink,
              lineHeight: 1.15,
            }}
          >
            {title}
          </Txt>
          <Flx ac g={1.25} wrap>
            <Txt
              sx={{
                fontFamily: FONT_MONO,
                fontSize: 12,
                color: T.ink2,
                background: T.surface2,
                px: 1,
                py: "2px",
                borderRadius: "4px",
                border: `1px solid ${T.line2}`,
              }}
            >
              #{loan?.loanNumber || "—"}
            </Txt>
            {loan?.loanProductType && <DotSep />}
            {loan?.loanProductType && (
              <Txt
                sx={{
                  background: T.infoSoft,
                  color: T.info,
                  border: `1px solid ${T.infoLine}`,
                  px: 1,
                  py: "2px",
                  borderRadius: "4px",
                  fontSize: 11,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontFamily: FONT_MONO,
                }}
              >
                {loan.loanProductType}
              </Txt>
            )}
            {loan?.loanPurpose && <DotSep />}
            {loan?.loanPurpose && (
              <Txt sx={{ fontSize: 13, color: T.ink3 }}>{loan.loanPurpose}</Txt>
            )}
            {loan?.createDateTime && <DotSep />}
            {loan?.createDateTime && (
              <Txt sx={{ fontSize: 13, color: T.ink3 }}>
                Created {formatDate(loan.createDateTime)}
                {lastUpdatedRel ? ` · last updated ${lastUpdatedRel}` : ""}
              </Txt>
            )}
          </Flx>
        </Box>

        <StatusChanger loan={loan} />
      </Flx>

      <MilestoneStageTracker loan={loan} />

      <Box sx={{ mt: 3, pt: 3 }}>
        <MetricsStrip loan={loan} />
      </Box>
      <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${T.line2}` }}>
        <AssignedTeamStrip loan={loan} />
      </Box>
    </Box>
  );
};

const DotSep = () => (
  <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: T.ink4 }} />
);

/* ─── Milestone-styled stage tracker ───────────────────────
   Visual style matches the Milestone Progression timeline in
   the Key Dates tab (see CombinedTimelineCard): top row of
   labels + dates + drop-pins, a thin ruler with progress
   fill, and a bottom row of stage segments with status-tinted
   backgrounds + dashed borders for future stages. Widths are
   equal — they don't reflect actual stage duration. */

// Exported (with a short `abbr` for compact renderers) so the drilldown
// header's LoanStageProgressBar shows the EXACT same stage set — one source of
// truth, so the header bar and this Summary tracker can never disagree.
export const BASE_STAGES = [
  { key: "Lead", label: "Lead", abbr: "LEAD" },
  { key: "Terms Accepted", label: "Terms accepted", abbr: "TERMS" },
  { key: "Processing", label: "Processing", abbr: "PROC" },
  { key: "Underwriting", label: "Underwriting", abbr: "UW" },
  { key: "Condition Clearance", label: "Cond. clearance", abbr: "COND" },
  { key: "Final Review", label: "Final review", abbr: "REVIEW" },
  { key: "Clear To Close", label: "Clear to close", abbr: "CTC" },
];

// Final states the loan can land in. None of these belong in BASE_STAGES;
// they're appended dynamically when the loan reaches them so the bar shows
// the actual outcome ("Closed" / "Inactive" / "Dead") at the end of the row.
// `tone` drives the segment color: success = accent green, error = warn
// amber, urgent = red.
export const FINAL_STATES = {
  Closed: { key: "Closed", label: "Closed", tone: "success", abbr: "CLOSED" },
  Inactive: { key: "Inactive", label: "Inactive", tone: "error", abbr: "INACTIVE" },
  Dead: { key: "Dead", label: "Dead", tone: "error", abbr: "DEAD" },
};

export const SUB_TO_PARENT = {
  "Appraisal Ordered": "Processing",
  "Appraisal In": "Processing",
  "Pre-Underwrite": "Processing",
  "Docs Out": "Clear To Close",
  "Docs Back": "Clear To Close",
  // Legacy data sometimes stores funded sub-statuses in loanStatus directly.
  // Modern path is loanStatus="Closed" + subStatus="Funded".
  "Funded with Post Close Conditions": "Closed",
  Funded: "Closed",
};

const MilestoneStageTracker = ({ loan }) => {
  const currentStatus = loan?.loanStatus;
  const effectiveCurrent = SUB_TO_PARENT[currentStatus] || currentStatus;

  // When the loan has reached a final state (Closed / Inactive / Dead),
  // append it to the stage list so it has its own segment at the end of the
  // bar. Earlier stages are then all rendered as "done".
  const finalState = FINAL_STATES[effectiveCurrent] || null;
  const STAGES = useMemo(
    () => (finalState ? [...BASE_STAGES, finalState] : BASE_STAGES),
    [finalState],
  );
  const currentIdx = STAGES.findIndex((s) => s.key === effectiveCurrent);

  // Sub-status — typically `loan.subStatus` (e.g. "Pre-Underwrite" while
  // loanStatus="Processing", or "Funded" while loanStatus="Closed"), but
  // legacy data sometimes stores the sub-status directly in `loanStatus`
  // (caught by SUB_TO_PARENT). Surface either form so the current-stage
  // segment can show it.
  const subStatusText =
    loan?.subStatus || (SUB_TO_PARENT[currentStatus] ? currentStatus : null);

  // Earliest milestoneStatusChanges entry per parent stage — same logic
  // as the existing AltTab StageTracker.
  const stageEnteredMs = useMemo(() => {
    const map = {};
    const changes = loan?.milestoneStatusChanges || [];
    for (const c of changes) {
      const updated = c?.updatedMilestoneStatus;
      if (!updated) continue;
      const parent = SUB_TO_PARENT[updated] || updated;
      const raw = c?.milestoneChangeDate;
      const t = raw ? new Date(raw).getTime() : null;
      if (t == null || Number.isNaN(t)) continue;
      if (map[parent] == null || t < map[parent]) {
        map[parent] = t;
      }
    }
    return map;
  }, [loan?.milestoneStatusChanges]);

  const rows = STAGES.map((s, idx) => {
    let state = "future";
    if (currentIdx >= 0) {
      if (idx < currentIdx) state = "done";
      else if (idx === currentIdx) state = "current";
    }
    // Carry the final-state tone onto the row when it's the appended final
    // segment, so the renderer can pick a non-default color for Closed /
    // Inactive / Dead.
    const tone = s.tone || null;
    let dateText = "—";
    let dateMeta = null;
    const enteredMs = stageEnteredMs[s.key];
    if (enteredMs != null) {
      dateText = formatDate(new Date(enteredMs).toISOString()).split(",")[0];
    } else if (idx === 0 && loan?.createDateTime) {
      dateText = formatDate(loan.createDateTime).split(",")[0];
    } else if (
      s.key === "Closed" &&
      state === "current" &&
      (loan?.fundedDate || loan?.originationDate)
    ) {
      // Prefer fundedDate (funds-out) → originationDate (actual closing) as
      // the "Closed" stage marker date when no explicit transition into
      // "Closed" was logged.
      const src = loan?.fundedDate || loan?.originationDate;
      dateText = formatDate(src).split(",")[0];
    } else if (
      s.key === "Clear To Close" &&
      state === "future" &&
      loan?.expectedClosingDate
    ) {
      dateText = formatDate(loan.expectedClosingDate).split(",")[0];
      dateMeta = "target";
    }

    // Effective start ms — used to compute how long this stage has lasted.
    // For "Lead" we fall back to the loan's createDateTime when no explicit
    // transition into Lead was logged, mirroring the dateText fallback.
    let startMs = enteredMs;
    if (startMs == null && idx === 0 && loan?.createDateTime) {
      const t = new Date(loan.createDateTime).getTime();
      if (!Number.isNaN(t)) startMs = t;
    }
    if (
      startMs == null &&
      s.key === "Closed" &&
      state === "current" &&
      (loan?.fundedDate || loan?.originationDate)
    ) {
      const t = new Date(loan?.fundedDate || loan?.originationDate).getTime();
      if (!Number.isNaN(t)) startMs = t;
    }
    return { ...s, state, dateText, dateMeta, startMs, tone };
  });

  // Stage durations: done stages run from their enteredMs to the next stage's
  // enteredMs; the current stage runs from its enteredMs to now. Stages
  // without a known start, and future stages, get null.
  const now = Date.now();
  rows.forEach((r, idx) => {
    if (r.startMs == null) {
      r.durationMs = null;
      return;
    }
    if (r.state === "future") {
      r.durationMs = null;
      return;
    }
    if (r.state === "current") {
      r.durationMs = Math.max(0, now - r.startMs);
      return;
    }
    // done — find next stage that has a known startMs
    let nextStartMs = null;
    for (let j = idx + 1; j < rows.length; j++) {
      if (rows[j].startMs != null) {
        nextStartMs = rows[j].startMs;
        break;
      }
    }
    r.durationMs =
      nextStartMs != null ? Math.max(0, nextStartMs - r.startMs) : null;
  });

  // Equal-width stage columns for the bottom track.
  const colWidthPct = 100 / STAGES.length;

  // ── Date-anchored top track ────────────────────────────
  // Only three markers: Origination (loan created), Today, and Expected
  // Closing. Positions reflect actual dates along the loan's timeline,
  // independent of the equal-width stage segments below.
  const startMs = useMemo(() => {
    const v = loan?.createDateTime;
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
  }, [loan?.createDateTime]);

  // For closed loans, prefer the actual close-out date (fundedDate →
  // originationDate) over expectedClosingDate so the timeline reflects what
  // really happened. Pending loans stick with expectedClosingDate as the
  // forward-looking target.
  const closedEndSource = useMemo(() => {
    if (!finalState || finalState.tone !== "success") return null;
    return loan?.fundedDate || loan?.originationDate || null;
  }, [finalState, loan?.fundedDate, loan?.originationDate]);

  const endMs = useMemo(() => {
    const v = closedEndSource || loan?.expectedClosingDate;
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
  }, [closedEndSource, loan?.expectedClosingDate]);

  const endLabel = closedEndSource ? "Closed" : "Expected Closing";
  const endDateSource = closedEndSource || loan?.expectedClosingDate;

  const nowMs = Date.now();
  const dateSpan =
    startMs != null && endMs != null && endMs > startMs
      ? endMs - startMs
      : null;
  const todayDatePct =
    dateSpan != null
      ? Math.max(0, Math.min(100, ((nowMs - startMs) / dateSpan) * 100))
      : startMs != null && endMs != null
        ? 50 // degenerate timeline (start >= end) — center today as a fallback
        : null;

  const topMilestones = [];
  if (startMs != null) {
    topMilestones.push({
      key: "origination",
      label: "Origination",
      dateText: formatDate(loan.createDateTime).split(",")[0],
      leftPct: 0,
      state: "done",
    });
  }
  // Today merges into the closer edge milestone when it would overlap.
  // Threshold: <8% of timeline width on either side. The merged label
  // stacks "Today" above the edge label, so the user still sees "TODAY"
  // without two competing labels at the same point.
  const NEAR_EDGE_THRESHOLD = 8;
  let todayMergedInto = null; // "origination" | "closing" | null
  if (todayDatePct != null) {
    if (startMs != null && todayDatePct <= NEAR_EDGE_THRESHOLD) {
      todayMergedInto = "origination";
    } else if (endMs != null && todayDatePct >= 100 - NEAR_EDGE_THRESHOLD) {
      todayMergedInto = "closing";
    } else {
      topMilestones.push({
        key: "today",
        label: "Today",
        dateText: formatDate(new Date(nowMs).toISOString()).split(",")[0],
        leftPct: todayDatePct,
        state: "today",
      });
    }
  }
  if (endMs != null) {
    const pastDue = !closedEndSource && nowMs > endMs;
    topMilestones.push({
      key: "closing",
      label: endLabel,
      dateText: formatDate(endDateSource).split(",")[0],
      leftPct: 100,
      // Closed loans render the end marker as "done" (success); pending
      // loans use "future" until reached, with "warn" once past due.
      state: closedEndSource ? "done" : pastDue ? "warn" : "future",
      meta: pastDue ? "past due" : null,
      hasToday: todayMergedInto === "closing",
      todayDateText:
        todayMergedInto === "closing"
          ? formatDate(new Date(nowMs).toISOString()).split(",")[0]
          : null,
    });
  }
  // Apply merge to Origination too (when today is right at the start).
  if (todayMergedInto === "origination") {
    const origin = topMilestones.find((m) => m.key === "origination");
    if (origin) {
      origin.hasToday = true;
      origin.todayDateText = formatDate(new Date(nowMs).toISOString()).split(
        ",",
      )[0];
    }
  }

  // Ruler fill spans 0% → today's position on the date timeline.
  const rulerFillPct = todayDatePct ?? 0;

  return (
    <Box sx={{ position: "relative" }}>
      {/* Top track — Origination, Today, Expected Closing. Edge milestones
        anchor to the card edge so labels don't overflow when there's no
        outer padding. */}
      <Box sx={{ position: "relative", height: 60 }}>
        {topMilestones.map((m) => {
          const isToday = m.state === "today";
          const isDone = m.state === "done";
          const isWarn = m.state === "warn";
          const pinColor = isWarn
            ? T.warn
            : isToday
              ? T.ink
              : isDone
                ? T.accent
                : T.ink4;
          const dateColor = isWarn
            ? T.warn
            : isToday
              ? T.ink
              : isDone
                ? T.ink
                : T.ink2;
          const labelColor = isToday ? T.ink : T.ink3;

          // Anchor to nearest edge for milestones at 0% / 100% so the
          // label sits inside the card; pin still drops from leftPct.
          const isStart = m.leftPct <= 1;
          const isEnd = m.leftPct >= 99;
          const containerSx = isStart
            ? { left: 0, alignItems: "flex-start", textAlign: "left" }
            : isEnd
              ? {
                  right: 0,
                  alignItems: "flex-end",
                  textAlign: "right",
                }
              : {
                  left: `${m.leftPct}%`,
                  transform: "translateX(-50%)",
                  alignItems: "center",
                  textAlign: "center",
                };

          return (
            <Box
              key={m.key}
              sx={{
                position: "absolute",
                top: 0,
                width: "max-content",
                maxWidth: 160,
                display: "flex",
                flexDirection: "column",
                ...containerSx,
              }}
            >
              {/* Merged TODAY tag — when Today's date sits within
                NEAR_EDGE_THRESHOLD of this milestone, surface it
                as a small tag above the milestone's main label
                rather than rendering a competing milestone block. */}
              {m.hasToday && (
                <Flx
                  ac
                  g={0.5}
                  sx={{
                    mb: "2px",
                    justifyContent: isStart
                      ? "flex-start"
                      : isEnd
                        ? "flex-end"
                        : "center",
                    fontSize: 10,
                    color: T.ink,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 700,
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: T.ink,
                    }}
                  />
                  Today · {m.todayDateText}
                </Flx>
              )}
              <Txt
                component="div"
                sx={{
                  fontSize: 10.5,
                  color: labelColor,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: isToday ? 700 : 500,
                  lineHeight: 1.15,
                }}
              >
                {m.label}
              </Txt>
              <Txt
                component="div"
                sx={{
                  mt: "2px",
                  fontSize: 11.5,
                  color: dateColor,
                  fontFamily: FONT_MONO,
                  fontWeight: isToday || isWarn ? 700 : isDone ? 500 : 400,
                  whiteSpace: "nowrap",
                }}
              >
                {m.dateText}
                {m.meta && (
                  <Box
                    component="span"
                    sx={{
                      ml: "6px",
                      color: isWarn ? T.warn : T.ink3,
                      fontWeight: isWarn ? 600 : 400,
                    }}
                  >
                    · {m.meta}
                  </Box>
                )}
              </Txt>
              <Box
                sx={{
                  mt: "4px",
                  width: isToday ? "2px" : "1px",
                  height: 16,
                  background: pinColor,
                  // Push edge pins back to the date position they represent
                  // (the label container itself is anchored to the card edge).
                  ...(isStart
                    ? { alignSelf: "flex-start" }
                    : isEnd
                      ? { alignSelf: "flex-end" }
                      : {}),
                }}
              />
            </Box>
          );
        })}
      </Box>

      {/* Shared ruler — 4px bar with progress fill 0% → today's date. */}
      <Box
        sx={{
          position: "relative",
          height: 4,
          background: T.line2,
          borderRadius: "2px",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${rulerFillPct}%`,
            background: T.accent,
            borderRadius: "2px",
          }}
        />
      </Box>

      {/* Bottom track — equal-width stage segments. */}
      <Box sx={{ position: "relative", height: 38, mt: "8px" }}>
        {rows.map((r, idx) => {
          const isFuture = r.state === "future";
          const isCurrent = r.state === "current";
          // Final-state tone overrides the default accent palette for the
          // current segment when the loan ended in Inactive / Dead. Closed
          // shares the green accent, so its tone falls through to the
          // default branch.
          const isErrorTone = isCurrent && r.tone === "error";
          const errorColor = r.key === "Dead" ? T.urgent : T.warn;
          const errorSoft = r.key === "Dead" ? T.urgentSoft : T.warnSoft;
          const bg = isErrorTone
            ? errorColor
            : isCurrent
              ? T.accent
              : isFuture
                ? T.surface2
                : T.accentSoft;
          const fg = isErrorTone
            ? T.surface
            : isCurrent
              ? T.surface
              : isFuture
                ? T.ink3
                : T.accent;
          const border = isErrorTone
            ? `1px solid ${errorColor}`
            : isFuture
              ? `1px dashed ${T.ink4}`
              : `1px solid ${T.accent}`;
          // Suppress unused-warning for errorSoft — kept for future use if
          // we want a softer tint on done-error states.
          void errorSoft;
          const leftPct = idx * colWidthPct;
          return (
            <Box
              key={r.key}
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `calc(${leftPct}% + 2px)`,
                width: `calc(${colWidthPct}% - 4px)`,
                background: bg,
                border,
                borderRadius: "4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                px: "6px",
                overflow: "hidden",
                gap: "1px",
              }}
            >
              <Txt
                component="span"
                sx={{
                  fontSize: 10.5,
                  fontWeight: isCurrent ? 700 : 600,
                  color: fg,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  lineHeight: 1.1,
                }}
              >
                {r.label}
              </Txt>
              <Txt
                component="span"
                sx={{
                  fontSize: 9.5,
                  fontWeight: 500,
                  color: fg,
                  opacity: 0.75,
                  fontFamily: FONT_MONO,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  lineHeight: 1.1,
                }}
              >
                {(() => {
                  const dur = fmtDuration(r.durationMs);
                  // Current stage with a sub-status: lead with the sub-status
                  // name and append the running duration when we have one.
                  // ("Pre-Underwrite · 3d so far" / "Pre-Underwrite")
                  if (isCurrent && subStatusText) {
                    return dur ? `${subStatusText} · ${dur}` : subStatusText;
                  }
                  if (dur) return isCurrent ? `${dur} so far` : dur;
                  return isCurrent ? "Current" : isFuture ? "Upcoming" : "Done";
                })()}
              </Txt>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ─── Metrics strip ────────────────────────────────────────
   Headline figures from the reference screenshot, laid out as
   a responsive grid. Two visual rows on wide screens, collapsing
   to fewer columns on narrow viewports.

   Formulas reference LOAN_DATA_MODEL.md §5 / §13. Cost to ARV
   is derived client-side (no backend computed_field). */
const MetricsStrip = ({ loan }) => {
  const f = useMemo(() => buildLoanFormulas(loan), [loan]);

  const metrics = [
    {
      label: "Total Loan Amount",
      value: dollar(loan?.totalLoanAmount),
      formula: f.totalLoanAmount,
    },
    {
      label: "Initial Loan Amount",
      value: dollar(loan?.baseLoanAmount),
    },
    {
      label: "Rehab Holdback",
      value: dollar(loan?.totalHoldback),
    },
    {
      label: "Interest Reserve",
      value: dollar(loan?.totalInterestReserves),
      formula: f.interestReserve,
    },
    {
      label: "Total Cost",
      value: dollar(loan?.totalCostBasis),
      formula: f.totalCost,
    },
    {
      label: "After Repair Value",
      value: dollar(loan?.totalArv),
      formula: f.afterRepairValue,
    },
    {
      label: "Purchase Price",
      value: dollar(loan?.totalPurchasePrice),
      formula: f.purchasePrice,
    },
    {
      label: "Cost to ARV",
      value: (() => {
        const tc = loan?.totalCostBasis;
        const arv = loan?.totalArv;
        if (isNil(tc) || isNil(arv) || arv === 0) return pct(null);
        return pct(tc / arv);
      })(),
      formula: f.costToArv,
    },
    {
      label: "Loan to Purchase Price",
      value: pct(loan?.baseLoanToPurchasePrice),
      formula: f.loanToPurchasePrice,
    },
    {
      label: "Loan to Value",
      value: pct(loan?.baseLoanToAsIsValue),
      formula: f.loanToValue,
    },
    {
      label: "Loan to Cost",
      value: pct(loan?.baseLoanToCost),
      formula: f.loanToCost,
    },
    {
      label: "Loan to ARV",
      value: pct(loan?.totalLoanToArv),
      formula: f.loanToArv,
    },
    {
      label: "Term",
      value: !isNil(loan?.termMonths) ? `${loan.termMonths}` : "—",
    },
    { label: "Note Rate", value: pct(loan?.interestRate) },
    { label: "Origination Points", value: pct(loan?.originationPoints) },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(3, 1fr)",
          md: "repeat(5, 1fr)",
          lg: "repeat(8, 1fr)",
          // lg: "repeat(9, 1fr)",
        },
        rowGap: "24px",
        columnGap: "24px",
        // rowGap: "18px",
        // columnGap: "14px",
      }}
    >
      {metrics.map((m) => (
        <MetricCell
          key={m.label}
          label={m.label}
          value={m.value}
          formula={m.formula}
        />
      ))}
    </Box>
  );
};

const MetricCell = ({ label, value, formula }) => (
  <Box sx={{ minWidth: 0 }}>
    <Txt
      sx={{
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: T.ink3,
        fontWeight: 500,
        mb: "4px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </Txt>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Txt
        sx={{
          fontFamily: FONT_MONO,
          fontSize: 14,
          fontWeight: 500,
          color: T.ink,
          letterSpacing: "-0.01em",
          lineHeight: 1.1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
        }}
      >
        {value || "—"}
      </Txt>
      {formula && (
        <Tooltip
          title={
            <Box
              sx={{
                whiteSpace: "pre-line",
                fontFamily: FONT_MONO,
                fontSize: 11.5,
                lineHeight: 1.5,
              }}
            >
              {formula}
            </Box>
          }
          arrow
          placement="top"
        >
          <Functions
            sx={{
              fontSize: 12,
              color: T.good,
              cursor: "help",
              flexShrink: 0,
            }}
          />
        </Tooltip>
      )}
    </Box>
  </Box>
);

export const BentoGrid = ({ children }) => (
  <Flx wrap g={3} sx={{ alignItems: "flex-start", mb: 3 }}>
    {children}
  </Flx>
);

export const BentoColumn = ({ flex, minWidth, children }) => (
  <Flx column g={3} sx={{ flex, minWidth }}>
    {children}
  </Flx>
);

/* ─── Field row ────────────────────────────────────────────
   Mirrors the typography of SummaryRedesignAltTab's InfoRow:
   uppercase label on the left, mono value on the right, with
   an optional Σ icon for computed values. */
const FieldRow = ({ label, value, computed, computedTooltip, isLast }) => (
  <Flx
    ac
    jb
    g={1.5}
    sx={{
      px: "20px",
      py: "10px",
      borderBottom: isLast ? "none" : `1px solid ${T.line2}`,
    }}
  >
    <Txt
      sx={{
        fontSize: 11.5,
        color: T.ink3,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 500,
      }}
    >
      {label}
    </Txt>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        textAlign: "right",
        fontWeight: 500,
        fontSize: 13,
        color: T.ink,
        fontFamily: FONT_MONO,
        letterSpacing: "-0.01em",
        lineHeight: 1.1,
      }}
    >
      {computed && (
        <Tooltip
          title={
            computedTooltip ? (
              <Box
                sx={{
                  whiteSpace: "pre-line",
                  fontFamily: FONT_MONO,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                }}
              >
                {computedTooltip}
              </Box>
            ) : (
              "Auto-calculated value"
            )
          }
          arrow
          placement="left"
        >
          <Functions
            sx={{
              fontSize: 13,
              color: T.good,
              cursor: "help",
              flexShrink: 0,
            }}
          />
        </Tooltip>
      )}
      <span>{value || "—"}</span>
    </Box>
  </Flx>
);

const FieldsCard = ({ title, rows, endContent }) => (
  <Card>
    <CardHead title={title} endContent={endContent} />
    <Box>
      {rows.map((row, idx) => (
        <FieldRow key={row.label} {...row} isLast={idx === rows.length - 1} />
      ))}
    </Box>
  </Card>
);

const TwoColFieldsCard = ({ title, rows, endContent }) => {
  const colCount = 2;
  const lastIdxs = new Set();
  for (let c = 0; c < colCount; c++) {
    const items = rows.filter((_, i) => i % colCount === c);
    if (items.length > 0) {
      const lastInColAbs = (items.length - 1) * colCount + c;
      lastIdxs.add(lastInColAbs);
    }
  }
  return (
    <Card>
      <CardHead title={title} endContent={endContent} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        {rows.map((row, idx) => (
          <FieldRow key={row.label} {...row} isLast={lastIdxs.has(idx)} />
        ))}
      </Box>
    </Card>
  );
};

// Row-builder hooks are exported alongside the cards so other tabs (e.g.
// Summary Redesign 3) can render the SAME data inside their own card chrome.
export const useQuickAttributesRows = (loan) => [
  { label: "Loan Purpose", value: loan?.loanPurpose },
  { label: "Loan Type", value: loan?.loanProductType },
  { label: "Exit Strategy", value: loan?.exitStrategy },
];

export const QuickAttributesCard = ({ loan }) => (
  <FieldsCard title="Quick attributes" rows={useQuickAttributesRows(loan)} />
);

export const useLoanInformationOverviewRows = (loan) => {
  const f = useMemo(() => buildLoanFormulas(loan), [loan]);
  // Rehab holdback + construction interest reserve are hidden for DSCR.
  const construction = isConstructionProduct(loan?.loanProductType);
  return [
    {
      label: "Total loan amount",
      value: dollar(loan?.totalLoanAmount),
      computed: true,
      computedTooltip: f.totalLoanAmount,
    },
    { label: "Initial loan amount", value: dollar(loan?.baseLoanAmount) },
    ...(construction
      ? [
          { label: "Rehab holdback", value: dollar(loan?.totalHoldback) },
          {
            label: "Interest (payment) reserve",
            value: dollar(loan?.totalInterestReserves),
            computed: true,
            computedTooltip: f.interestReserve,
          },
        ]
      : []),
    {
      label: "Loan term",
      value: !isNil(loan?.termMonths) ? `${loan.termMonths} mo` : "—",
    },
    { label: "Note rate", value: pct(loan?.interestRate) },
    { label: "Interest calc type", value: loan?.interestCalcType },
    { label: "Origination points", value: pct(loan?.originationPoints) },
    { label: "Discount points", value: pct(loan?.discountPoints) },
    {
      label: "Broker origination",
      value: pct(loan?.brokerOriginationPoints),
    },
  ];
};

export const LoanInformationOverviewCard = ({ loan }) => (
  <FieldsCard
    title="Loan information overview"
    rows={useLoanInformationOverviewRows(loan)}
  />
);

export const useInterestReserveRows = (loan) => {
  const f = useMemo(() => buildLoanFormulas(loan), [loan]);

  const monthlyPaymentFullyDrawn = useMemo(() => {
    const total = loan?.totalLoanAmount;
    const rate = loan?.interestRate;
    if (isNil(total) || isNil(rate)) return null;
    return (total * rate) / 12;
  }, [loan?.totalLoanAmount, loan?.interestRate]);

  const monthsOfReserve = useMemo(() => {
    if (isNil(loan?.totalInterestReserves) || !monthlyPaymentFullyDrawn) {
      return null;
    }
    return loan.totalInterestReserves / monthlyPaymentFullyDrawn;
  }, [loan?.totalInterestReserves, monthlyPaymentFullyDrawn]);

  return [
    {
      label: "# of months",
      value: !isNil(monthsOfReserve) ? monthsOfReserve.toFixed(2) : "—",
      computed: true,
      computedTooltip: f.monthsOfReserve,
    },
    {
      label: "Monthly payment (fully drawn)",
      value: dollar(monthlyPaymentFullyDrawn),
      computed: true,
      computedTooltip: f.monthlyPayment,
    },
    {
      label: "Total interest reserves",
      value: dollar(loan?.totalInterestReserves),
      computed: true,
      computedTooltip: f.interestReserve,
    },
  ];
};

export const InterestReserveCard = ({ loan }) => (
  <FieldsCard
    title="Interest (payment) reserve"
    rows={useInterestReserveRows(loan)}
  />
);

export const usePurchaseRows = (loan) => {
  const f = useMemo(() => buildLoanFormulas(loan), [loan]);
  // Rehab budget + LTC are construction economics — hidden for DSCR.
  const construction = isConstructionProduct(loan?.loanProductType);

  const totalRehab = loan?.totalConstructionBudget;
  const totalCost = useMemo(() => {
    const purchase = loan?.totalPurchasePrice;
    if (isNil(purchase) && isNil(totalRehab)) return null;
    return (purchase || 0) + (totalRehab || 0);
  }, [loan?.totalPurchasePrice, totalRehab]);

  return [
    {
      label: "Total purchase price",
      value: dollar(loan?.totalPurchasePrice),
      computed: true,
      computedTooltip: f.purchasePrice,
    },
    {
      label: "Assignment fees",
      value: dollar(loan?.totalAssignmentFees),
      computed: true,
      computedTooltip: f.assignmentFees,
    },
    ...(construction
      ? [
          {
            label: "Total rehab budget",
            value: dollar(totalRehab),
            computed: true,
            computedTooltip: f.totalRehab,
          },
          {
            label: "Cost (purchase + rehab)",
            value: dollar(totalCost),
            computed: true,
            computedTooltip: f.cost,
          },
          {
            label: "LTC",
            value: pct(loan?.baseLoanToCost),
            computed: true,
            computedTooltip: f.loanToCost,
          },
        ]
      : []),
    {
      label: "LTV (as-is)",
      value: pct(loan?.baseLoanToAsIsValue),
      computed: true,
      computedTooltip: f.loanToValue,
    },
  ];
};

export const PurchaseCard = ({ loan }) => (
  <FieldsCard title="Purchase" rows={usePurchaseRows(loan)} />
);

export const useRefiRows = (loan) => {
  const f = useMemo(() => buildLoanFormulas(loan), [loan]);
  const firstProperty = loan?.subjectProperties?.[0];
  const propertyCount = loan?.subjectProperties?.length || 0;
  const showSinglePropertyHints = propertyCount === 1;

  return [
    {
      label: "Original purchase price",
      value: showSinglePropertyHints
        ? // purchasePrice is the original purchase on a refi; the loose
          // originalPurchasePrice key is only on properties saved by the old
          // add forms, read as a fallback.
          dollar(
            firstProperty?.purchasePrice ?? firstProperty?.originalPurchasePrice,
          )
        : "—",
    },
    {
      label: "Year purchased",
      value: showSinglePropertyHints
        ? firstProperty?.yearPurchased || "—"
        : "—",
    },
    {
      label: "Current mortgage balance",
      value: dollar(loan?.outstandingLoanUpb),
    },
    {
      label: "Current mortgage lender",
      value: showSinglePropertyHints
        ? firstProperty?.currentMortgageLender || "—"
        : "—",
    },
    {
      label: "Total cost basis",
      value: dollar(loan?.totalCostBasis),
      computed: true,
      computedTooltip: f.totalCost,
    },
  ];
};

export const RefiCard = ({ loan }) => (
  <FieldsCard title="Refinance" rows={useRefiRows(loan)} />
);

export const useRehabInfoRows = (loan) => {
  const f = useMemo(() => buildLoanFormulas(loan), [loan]);

  const totalRehab = loan?.totalConstructionBudget;
  const rehabHoldback = loan?.totalHoldback;
  const borrowerFinancedRehab = useMemo(() => {
    if (isNil(totalRehab) || isNil(rehabHoldback)) return null;
    return Math.max(0, totalRehab - rehabHoldback);
  }, [totalRehab, rehabHoldback]);

  return [
    {
      label: "Total rehab",
      value: dollar(totalRehab),
      computed: true,
      computedTooltip: f.totalRehab,
    },
    { label: "Rehab holdback", value: dollar(rehabHoldback) },
    {
      label: "Borrower financed rehab",
      value: dollar(borrowerFinancedRehab),
      computed: true,
      computedTooltip: f.borrowerFinancedRehab,
    },
    {
      label: "Total ARV",
      value: dollar(loan?.totalArv),
      computed: true,
      computedTooltip: f.afterRepairValue,
    },
    {
      label: "Total LTARV",
      value: pct(loan?.totalLoanToArv),
      computed: true,
      computedTooltip: f.loanToArv,
    },
  ];
};

export const RehabInfoCard = ({ loan }) => (
  <FieldsCard title="Rehab information" rows={useRehabInfoRows(loan)} />
);

/* ─── Horizontal Assigned Team card ────────────────────────
   Each role is a horizontal row (label + person + hover-edit
   icon), with hairline borders between rows — same chrome as
   DateRow in the Key Dates tab. Clicking the edit icon opens
   a small popover for reassignment. */
const ROLES = [
  { role: "Loan Officer", field: "salesperson", color: T.accent },
  { role: "Sales Analyst", field: "salesAnalyst", color: "#5e3aa8" },
  { role: "Underwriter", field: "underwriter", color: T.info },
  { role: "Processor", field: "processor", color: T.warn },
  { role: "Closer", field: "loanCloser", color: T.good },
];

// Build initials from a full name; falls back to first letters of the email
// local-part if no name resolves. Returns "?" when nothing is available.
const initialsFor = (fullName, email) => {
  const source =
    (fullName && fullName.trim()) ||
    (email ? email.split("@")[0].replace(/[._-]+/g, " ") : "");
  if (!source) return "?";
  return source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
};

const HorizontalAssignedTeamCard = ({ loan }) => {
  const users = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const { updateLoanData, loading } = useUnderwritingHook();
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingField, setEditingField] = useState(null);

  const handleOpen = (e, field) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setEditingField(field);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setEditingField(null);
  };
  const handleSave = (newEmail) => {
    if (!loan?._id || !editingField) return;
    updateLoanData({
      loanId: loan._id,
      data: { [editingField]: newEmail || null },
      onSuccessFn: (res) => {
        dispatch(loanDrilldownSet(res));
        handleClose();
      },
    });
  };

  return (
    <Card sx={{ overflow: "unset" }}>
      {/* <CardHead title="Assigned team" /> */}
      <Box>
        {ROLES.map((r, idx) => (
          <AssigneeHRow
            key={r.field}
            role={r.role}
            email={loan?.[r.field]}
            users={users}
            color={r.color}
            isFirst={idx === 0}
            onEditClick={(e) => handleOpen(e, r.field)}
          />
        ))}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${T.line}`,
              borderRadius: 2,
              boxShadow: SHADOW_MD,
              mt: 0.5,
            },
          },
        }}
      >
        {editingField && (
          <AssignPopoverContent
            role={ROLES.find((x) => x.field === editingField)?.role}
            currentEmail={loan?.[editingField]}
            saving={loading}
            onSave={handleSave}
            onClose={handleClose}
          />
        )}
      </Popover>
    </Card>
  );
};

const AssigneeHRow = ({ role, email, users, color, isFirst, onEditClick }) => {
  const fullName = email ? getUserFullNameFromEmail(users, email) : null;
  const empty = !email;
  const initials = empty ? "+" : initialsFor(fullName, email);
  return (
    <Flx
      ac
      g={1.5}
      sx={{
        px: "20px",
        py: "12px",
        borderTop: isFirst ? "none" : `1px solid ${T.line2}`,
        transition: "background 0.15s",
        "&:hover": { background: T.surface2 },
        "&:hover .row-edit-icon": { opacity: 1 },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 12,
          fontWeight: 600,
          textTransform: "uppercase",
          background: empty ? T.surface2 : color,
          color: empty ? T.ink4 : "#fff",
          border: empty ? `1px dashed ${T.ink4}` : "none",
          flexShrink: 0,
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Txt
          component="div"
          sx={{
            fontSize: 10.5,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: T.ink3,
            fontWeight: 500,
            mb: "2px",
          }}
        >
          {role}
        </Txt>
        <Txt
          component="div"
          sx={{
            fontSize: 13.5,
            color: empty ? T.ink3 : T.ink,
            fontWeight: empty ? 400 : 500,
            fontStyle: empty ? "italic" : "normal",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {empty ? "Not assigned" : fullName || email}
        </Txt>
      </Box>
      <Tooltip title={empty ? `Assign ${role}` : `Reassign ${role}`} arrow>
        <IconButton
          className="row-edit-icon"
          size="small"
          onClick={onEditClick}
          sx={{
            opacity: 0,
            transition: "opacity 0.15s",
            border: `1px solid ${T.line}`,
            borderRadius: "5px",
            width: 26,
            height: 26,
            color: T.ink3,
            "&:hover": { background: "#fff", color: T.ink },
            "&:focus-visible": { opacity: 1 },
          }}
        >
          <EditOutlined sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Flx>
  );
};

const AssignPopoverContent = ({
  role,
  currentEmail,
  saving,
  onSave,
  onClose,
}) => {
  const [draft, setDraft] = useState(currentEmail || null);
  return (
    <Box sx={{ p: 1.5, minWidth: 280 }}>
      <Txt
        component="div"
        sx={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: T.ink3,
          fontWeight: 500,
          mb: 1,
        }}
      >
        Assign {role}
      </Txt>
      <SelectUserEmailInput value={draft} onChange={setDraft} />
      <Flx ac jb sx={{ mt: 1.25 }}>
        <Button
          size="small"
          color="error"
          variant="text"
          disabled={saving || !currentEmail}
          onClick={() => onSave(null)}
        >
          Clear
        </Button>
        <Flx ac g={0.5}>
          <Button size="small" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            loading={saving}
            disabled={draft === currentEmail}
            onClick={() => onSave(draft)}
            sx={{
              background: T.accent,
              "&:hover": { background: "#234d3a" },
            }}
          >
            Save
          </Button>
        </Flx>
      </Flx>
    </Box>
  );
};

/* ─── Horizontal Assigned Team strip ───────────────────────
   Full-width 4-cell strip styled like LoanActivityCardHorizontal
   in the Key Dates tab — each role is an equal-width cell with
   vertical hairlines between, role label + small badge on the
   first line, name in mono on the second, and a hover edit
   affordance with a popover for reassignment. */
const AssignedTeamStrip = ({ loan }) => {
  const users = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const { updateLoanData, loading } = useUnderwritingHook();
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingField, setEditingField] = useState(null);

  const handleOpen = (e, field) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setEditingField(field);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setEditingField(null);
  };
  const handleSave = (newEmail) => {
    if (!loan?._id || !editingField) return;
    updateLoanData({
      loanId: loan._id,
      data: { [editingField]: newEmail || null },
      onSuccessFn: (res) => {
        dispatch(loanDrilldownSet(res));
        handleClose();
      },
    });
  };

  const assignedCount = ROLES.filter((r) => loan?.[r.field]).length;

  return (
    <>
      <Flx
        sx={{
          alignItems: "stretch",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {ROLES.map((r, idx) => (
          <AssigneeStripCell
            key={r.field}
            role={r.role}
            field={r.field}
            color={r.color}
            email={loan?.[r.field]}
            users={users}
            isFirst={idx === 0}
            onEditClick={(e) => handleOpen(e, r.field)}
          />
        ))}
        <PermittedUsersCell loan={loan} isFirst={false} />
      </Flx>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${T.line}`,
              borderRadius: 2,
              boxShadow: SHADOW_MD,
              mt: 0.5,
            },
          },
        }}
      >
        {editingField && (
          <AssignPopoverContent
            role={ROLES.find((x) => x.field === editingField)?.role}
            currentEmail={loan?.[editingField]}
            saving={loading}
            onSave={handleSave}
            onClose={handleClose}
          />
        )}
      </Popover>
    </>
  );
  return (
    <Card sx={{ mb: 3, boxShadow: SHADOW_MD }}>
      <CardHead
        title="Assigned team"
        count={`${assignedCount}/${ROLES.length}`}
      />
      <Flx
        sx={{
          alignItems: "stretch",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {ROLES.map((r, idx) => (
          <AssigneeStripCell
            key={r.field}
            role={r.role}
            field={r.field}
            color={r.color}
            email={loan?.[r.field]}
            users={users}
            isFirst={idx === 0}
            onEditClick={(e) => handleOpen(e, r.field)}
          />
        ))}
      </Flx>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              border: `1px solid ${T.line}`,
              borderRadius: 2,
              boxShadow: SHADOW_MD,
              mt: 0.5,
            },
          },
        }}
      >
        {editingField && (
          <AssignPopoverContent
            role={ROLES.find((x) => x.field === editingField)?.role}
            currentEmail={loan?.[editingField]}
            saving={loading}
            onSave={handleSave}
            onClose={handleClose}
          />
        )}
      </Popover>
    </Card>
  );
};

const AssigneeStripCell = ({
  role,
  email,
  color,
  users,
  isFirst,
  onEditClick,
}) => {
  const fullName = email ? getUserFullNameFromEmail(users, email) : null;
  const empty = !email;
  const initials = empty ? "+" : initialsFor(fullName, email);
  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        px: "20px",
        py: "16px",
        borderLeft: isFirst ? "none" : `1px solid ${T.line2}`,
        position: "relative",
        transition: "background 0.15s",
        "&:hover": { background: T.surface2 },
        "&:hover .strip-edit-icon": { opacity: 1 },
        "@media (max-width: 899px)": {
          borderLeft: "none",
          borderTop: isFirst ? "none" : `1px solid ${T.line2}`,
        },
      }}
    >
      <Flx ac jb g={1} sx={{ alignItems: "baseline", mb: "8px" }}>
        <Txt
          component="div"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: T.ink3,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            flex: 1,
          }}
        >
          {role}
        </Txt>
        <Tooltip title={empty ? `Assign ${role}` : `Reassign ${role}`} arrow>
          <IconButton
            className="strip-edit-icon"
            size="small"
            onClick={onEditClick}
            sx={{
              opacity: 0,
              transition: "opacity 0.15s",
              border: `1px solid ${T.line}`,
              borderRadius: "5px",
              width: 22,
              height: 22,
              color: T.ink3,
              flexShrink: 0,
              "&:hover": { background: "#fff", color: T.ink },
              "&:focus-visible": { opacity: 1 },
            }}
          >
            <EditOutlined sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Flx>
      <Flx ac g={1.25} sx={{ minWidth: 0 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 12,
            fontWeight: 600,
            textTransform: "uppercase",
            background: empty ? T.surface2 : color,
            color: empty ? T.ink4 : "#fff",
            border: empty ? `1px dashed ${T.ink4}` : "none",
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Txt
            component="div"
            sx={{
              fontFamily: FONT_MONO,
              fontSize: 13,
              fontWeight: 500,
              color: empty ? T.ink3 : T.ink,
              fontStyle: empty ? "italic" : "normal",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {empty ? "Not assigned" : fullName || email}
          </Txt>
          {!empty && fullName && (
            <Txt
              component="div"
              sx={{
                mt: "2px",
                fontSize: 11,
                color: T.ink3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {email}
            </Txt>
          )}
        </Box>
      </Flx>
    </Box>
  );
};

/* ─── Permitted users cell ─────────────────────────────────
   Sits at the end of AssignedTeamStrip. Shows an overlapping
   stack of avatars (each with a hover tooltip resolving to
   "Full Name") for everyone in loan.permittedUsers. The strip
   edit icon opens a modal that reuses the same form/save flow
   as the header LoanPermittedUsersAvatarStack. */
const PermittedUsersCell = ({ loan, isFirst }) => {
  const users = useSelector((state) => state.users);
  const [showModal, setShowModal] = useState(false);

  const permittedUsers = loan?.permittedUsers || [];

  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        px: "20px",
        py: "16px",
        borderLeft: isFirst ? "none" : `1px solid ${T.line2}`,
        position: "relative",
        transition: "background 0.15s",
        "&:hover": { background: T.surface2 },
        "&:hover .strip-edit-icon": { opacity: 1 },
        "@media (max-width: 899px)": {
          borderLeft: "none",
          borderTop: isFirst ? "none" : `1px solid ${T.line2}`,
        },
      }}
    >
      <Flx ac jb g={1} sx={{ alignItems: "baseline", mb: "8px" }}>
        <Txt
          component="div"
          sx={{
            fontSize: 10.5,
            fontWeight: 600,
            color: T.ink3,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
            flex: 1,
          }}
        >
          Permitted Users
          {permittedUsers.length > 0 && (
            <Box
              component="span"
              sx={{
                ml: 0.75,
                color: T.ink4,
                fontWeight: 500,
                fontFamily: FONT_MONO,
              }}
            >
              · {permittedUsers.length}
            </Box>
          )}
        </Txt>
        <Tooltip title="Manage permitted users" arrow>
          <IconButton
            className="strip-edit-icon"
            size="small"
            onClick={() => setShowModal(true)}
            sx={{
              opacity: 0,
              transition: "opacity 0.15s",
              border: `1px solid ${T.line}`,
              borderRadius: "5px",
              width: 22,
              height: 22,
              color: T.ink3,
              flexShrink: 0,
              "&:hover": { background: "#fff", color: T.ink },
              "&:focus-visible": { opacity: 1 },
            }}
          >
            <EditOutlined sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Flx>

      {permittedUsers.length === 0 ? (
        <Txt
          sx={{
            fontSize: 13,
            color: T.ink3,
            fontStyle: "italic",
          }}
        >
          No permitted users
        </Txt>
      ) : (
        <Flx ac wrap sx={{ minHeight: 32 }}>
          {permittedUsers.map((email, i) => (
            <UserAvatar
              key={email}
              size={32}
              showTooltip
              label={getUserFullNameFromEmail(users, email) || email}
              sx={{
                border: `2px solid ${T.surface}`,
                marginLeft: i === 0 ? 0 : "-8px",
                background: "#5e3aa8",
                color: "#fff",
                cursor: "default",
              }}
            />
          ))}
        </Flx>
      )}

      <EditPermittedUsersModal
        show={showModal}
        onClose={() => setShowModal(false)}
        loan={loan}
      />
    </Box>
  );
};

/* ─── Permitted-users editor ───────────────────────────────
   Implements the UX recommended in LOAN_PERMITTED_USERS_GUIDE.md:

   - Renders the union of `permittedUsers` + the four staffing
     emails, deduplicated and lower-cased for matching. (The
     guide §6 "Listing access" pattern.)
   - Tags each entry with role chips when the email matches a
     staffing field (Loan Officer / Underwriter / Processor /
     Closer); otherwise marks it as "Manual".
   - Shows a lock icon on staffing-implicit entries so users
     understand why they're not removable from this list — the
     backend's `roles_permitted_users()` will silently re-merge
     the email on the next save anyway.
   - Manual entries (no staffing role match) get a remove ✕
     button on hover. Staffing rows stay non-removable since
     the email would be re-merged on next save anyway.
   - All mutations send the full updated `permittedUsers` array
     to POST `/add_permitted_users/{loanId}` via
     `updateLoanPermittedUsers({ loanId, permittedUsers })`,
     so adds AND removes both work in a single round-trip. */

const STAFFING_ROLES = [
  { field: "salesperson", label: "Loan Officer" },
  { field: "salesAnalyst", label: "Sales Analyst" },
  { field: "underwriter", label: "Underwriter" },
  { field: "processor", label: "Processor" },
  { field: "loanCloser", label: "Closer" },
];

const lc = (v) => (v ? String(v).trim().toLowerCase() : "");

const EditPermittedUsersModal = ({ show, onClose, loan }) => {
  const users = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const { updateLoanPermittedUsers, loading } = useUnderwritingHook();
  const [draft, setDraft] = useState(null);
  const [busyEmail, setBusyEmail] = useState(null);

  // email (lowercased) → role labels[] derived from staffing fields.
  const rolesByEmail = useMemo(() => {
    const map = new Map();
    for (const { field, label } of STAFFING_ROLES) {
      const key = lc(loan?.[field]);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(label);
    }
    return map;
  }, [loan]);

  // Union of permittedUsers + staffing emails, dedup'd by lowercased email.
  // Preserves the original casing of whichever entry shows up first.
  const displayedUsers = useMemo(() => {
    const seen = new Set();
    const list = [];
    const all = [
      ...(loan?.permittedUsers || []),
      ...STAFFING_ROLES.map(({ field }) => loan?.[field]).filter(Boolean),
    ];
    for (const e of all) {
      const key = lc(e);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      list.push(e);
    }
    return list;
  }, [loan]);

  const alreadyHasEmail = (e) => {
    const key = lc(e);
    if (!key) return false;
    return displayedUsers.some((u) => lc(u) === key);
  };

  const draftIsDuplicate = draft && alreadyHasEmail(draft);

  // Single submit path for both adds and removes — the backend expects the
  // full updated `permittedUsers` array on every call. We always include the
  // current staffing emails too so they don't get accidentally dropped on a
  // round-trip (they'd be re-merged by the backend, but it's cleaner to
  // keep the wire payload self-consistent).
  const submitList = (nextList, { onSuccess } = {}) => {
    if (!loan?._id) return;
    updateLoanPermittedUsers({
      loanId: loan._id,
      permittedUsers: nextList,
      onSuccessFn: (res) => {
        dispatch(loanDrilldownSet(res));
        onSuccess?.(res);
      },
    });
  };

  const handleAdd = () => {
    if (!draft || draftIsDuplicate) return;
    const trimmed = draft.trim();
    // Build the new list by appending the new email to the current union.
    // Dedup is defensive — alreadyHasEmail guards above, but lower-casing
    // guarantees no near-duplicates (e.g. "Foo@x.com" vs "foo@x.com").
    const next = [];
    const seen = new Set();
    for (const e of [...displayedUsers, trimmed]) {
      const key = lc(e);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      next.push(e);
    }
    setBusyEmail(trimmed);
    submitList(next, {
      onSuccess: () => {
        setDraft(null);
        setBusyEmail(null);
      },
    });
  };

  const handleRemove = (email) => {
    if (!email) return;
    const key = lc(email);
    const next = displayedUsers.filter((e) => lc(e) !== key);
    setBusyEmail(email);
    submitList(next, {
      onSuccess: () => setBusyEmail(null),
    });
  };

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title="Manage Permitted Users"
      maxWidth="sm"
    >
      <Flx column g={2.5} sx={{ pt: 0.5 }}>
        {/* Info banner — explains the add-only constraint and that
            staffing-derived access is automatic. */}
        <Flx
          g={1.25}
          sx={{
            alignItems: "flex-start",
            p: 1.5,
            border: `1px solid ${T.infoLine}`,
            background: T.infoSoft,
            borderRadius: 1.5,
          }}
        >
          <InfoOutlined
            sx={{ fontSize: 18, color: T.info, mt: "1px", flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Txt
              component="div"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: T.info,
                mb: "2px",
              }}
            >
              How access works on this loan
            </Txt>
            <Txt
              component="div"
              sx={{
                fontSize: 11.5,
                color: T.ink2,
                lineHeight: 1.45,
              }}
            >
              Anyone listed below can view and edit this loan. Staffing roles
              are granted access automatically and can&rsquo;t be revoked from
              this list — reassign the role to remove that access.
              Manually-added users can&rsquo;t be removed yet; support for
              revocation is coming soon.
            </Txt>
          </Box>
        </Flx>

        {/* Current access list */}
        <Box>
          <Flx ac jb sx={{ mb: 1 }}>
            <Txt
              component="div"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: T.ink3,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Current access
            </Txt>
            <Txt
              component="div"
              sx={{
                fontSize: 11,
                fontFamily: FONT_MONO,
                color: T.ink3,
              }}
            >
              {displayedUsers.length} user
              {displayedUsers.length === 1 ? "" : "s"}
            </Txt>
          </Flx>
          {displayedUsers.length === 0 ? (
            <Txt
              sx={{
                fontSize: 12.5,
                color: T.ink3,
                fontStyle: "italic",
                px: 1.5,
                py: 1.25,
                border: `1px dashed ${T.line2}`,
                borderRadius: 1.5,
              }}
            >
              No users have access to this loan yet.
            </Txt>
          ) : (
            <Flx column g={0.75}>
              {displayedUsers.map((email) => (
                <PermittedUserRow
                  key={email}
                  email={email}
                  fullName={getUserFullNameFromEmail(users, email) || email}
                  roles={rolesByEmail.get(lc(email)) || []}
                  busy={lc(busyEmail) === lc(email) && loading}
                  disableActions={loading}
                  onRemove={() => handleRemove(email)}
                />
              ))}
            </Flx>
          )}
        </Box>

        {/* Grant access section */}
        <Box>
          <Txt
            component="div"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: T.ink3,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 1,
            }}
          >
            Grant access
          </Txt>
          <Flx ac g={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SelectUserEmailInput
                value={draft}
                onChange={setDraft}
                placeholder="Search teammates by name or email..."
              />
            </Box>
            <Button
              variant="contained"
              loading={loading}
              disabled={!draft || draftIsDuplicate}
              onClick={handleAdd}
              startIcon={<PersonAddAlt1Outlined />}
              sx={{
                background: T.accent,
                "&:hover": { background: "#234d3a" },
              }}
            >
              Add
            </Button>
          </Flx>
          {draftIsDuplicate && (
            <Txt
              sx={{
                mt: 0.75,
                fontSize: 11.5,
                color: T.warn,
                fontWeight: 500,
              }}
            >
              {getUserFullNameFromEmail(users, draft) || draft} already has
              access to this loan.
            </Txt>
          )}
        </Box>

        <Flx end>
          <Button onClick={onClose} sx={{ color: T.ink2 }}>
            Done
          </Button>
        </Flx>
      </Flx>
    </BasicModal>
  );
};

const PermittedUserRow = ({
  email,
  fullName,
  roles,
  busy,
  disableActions,
  onRemove,
}) => {
  const isImplicit = roles.length > 0;
  return (
    <Flx
      ac
      g={1.25}
      sx={{
        px: 1.5,
        py: 1.25,
        border: `1px solid ${isImplicit ? T.accentLine : T.line2}`,
        borderRadius: 1.5,
        background: isImplicit ? T.accentSoft : T.surface,
        opacity: busy ? 0.55 : 1,
        transition: "opacity 0.15s",
        "&:hover .row-remove-btn": { opacity: 1 },
      }}
    >
      <UserAvatar
        size={32}
        label={fullName}
        sx={{
          background: isImplicit ? T.accent : "#5e3aa8",
          color: "#fff",
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Flx ac g={0.5}>
          <Txt
            component="div"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: T.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {fullName}
          </Txt>
          {isImplicit && (
            <Tooltip
              title="Granted automatically by staffing role — reassign the role to remove this access."
              arrow
            >
              <LockOutlined
                sx={{ fontSize: 12, color: T.ink3, flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Flx>
        <Txt
          component="div"
          sx={{
            fontSize: 11.5,
            color: T.ink3,
            fontFamily: FONT_MONO,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {email}
        </Txt>
      </Box>
      <Flx ac g={0.5} sx={{ flexShrink: 0, flexWrap: "wrap" }}>
        {isImplicit ? (
          roles.map((r) => (
            <Box
              key={r}
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                px: 0.75,
                py: "2px",
                borderRadius: "3px",
                background: T.surface,
                color: T.accent,
                border: `1px solid ${T.accent}`,
                whiteSpace: "nowrap",
              }}
            >
              {r}
            </Box>
          ))
        ) : (
          <>
            <Box
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                px: 0.75,
                py: "2px",
                borderRadius: "3px",
                background: T.surface2,
                color: T.ink3,
                border: `1px solid ${T.line2}`,
                whiteSpace: "nowrap",
              }}
            >
              Manual
            </Box>
            <Tooltip title={`Remove ${fullName}`} arrow>
              <span>
                <IconButton
                  className="row-remove-btn"
                  size="small"
                  onClick={onRemove}
                  disabled={disableActions}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.15s",
                    width: 24,
                    height: 24,
                    color: T.ink3,
                    "&:hover": { background: T.warnSoft, color: T.warn },
                    "&:focus-visible": { opacity: 1 },
                  }}
                >
                  <Close sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Flx>
    </Flx>
  );
};

